import {print as astToString} from 'query-api-graphql';
import React, {useCallback, useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {DATA_FRAME_DISPLAY_ROW_LIMIT} from 'analysis-tool/AnalysisToolConstants';
import getDefaultMainViewItemForStep from 'analysis-tool/ui/getDefaultMainViewItemForStep';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {sum} from 'BenchlingUtil';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import {generateDatasetPreview} from 'query-api/QueryApiDAO';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  makeAsyncCall,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import useThrottle from 'ui-library/hooks/useThrottle';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as Logging from 'util/Logging';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {
  selectedColumnsToColumnMappings,
  unselectColumnsIfColumnIsNotInTemplateColumns,
  getTemplateDataFrameColumns,
} from '../column-mapping/ColumnMappingUtil';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {buildPreviewOverRowLimitMessage, buildPreviewFailureMessage} from '../DataFrameCreationMessages';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {
  getEnabledDatasetCreationMethods,
  getSharedDataFrameCreationMethodProps,
} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import buildQueryApiQueryAst from './buildQueryApiQueryAst';
import formStateToHumanReadableString from './formStateToHumanReadableString';
import {FormState} from './RegistrySchemaDataFrameCreationTypes';
import RegistrySchemaStepperForm from './RegistrySchemasStepperForm';

export const MAX_NUM_QUERY_API_FIELDS = 100;

/**
 * Not used by frontend except for messaging.
 *
 * Keep in sync with benchling/v3_api/query_api/datasets/dataset_builder.py
 */
const PREVIEW_LIMIT = 100;

function buildErrorAndWarningMessages(
  previewState: AsyncState<{dataFrame: DataFrame}>
): Array<InlineBannerProps> {
  if (previewState.status === AsyncStatus.ERROR) {
    return [buildPreviewFailureMessage()];
  }

  const previewData = getMostRecentData(previewState);
  if (previewData?.dataFrame == null) {
    return [];
  }

  if (previewData.dataFrame.rows.length >= PREVIEW_LIMIT) {
    return [
      buildPreviewOverRowLimitMessage(
        PREVIEW_LIMIT,
        `The final table will be limited to the first ${DATA_FRAME_DISPLAY_ROW_LIMIT.toLocaleString()} rows.`
      ),
    ];
  }

  return [];
}

// TODO(BNCH-53555): Handle and test dataset name editing once design conversations have resolved (see ticket
// for details/links)
const DEFAULT_DATA_FRAME_NAME_STATE = {
  name: '',
  hasUserEdits: false,
};
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);

const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};
const registrySchemaFormGlobalState = makeUseGlobalClientState<FormState>(DEFAULT_QUERY_FORM_STATE);

const previewGlobalState =
  makeUseGlobalClientState<AsyncState<{dataFrame: DataFrame}>>(NOT_FETCHED_ASYNC_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

/** @package */
export const FORM_STATE_THROTTLE_DURATION = 1000;

export default function RegistrySchemasDataFrameCreationView({
  analysis,
  clientStateId,
  onOpenStepInitializationView,
  onCloseStepInitializationView,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION>): JSX.Element {
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);

  const [queryFormState, setQueryFormState] =
    registrySchemaFormGlobalState.useGlobalClientState(clientStateId);

  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);

  const queryApiIntrospection = useQueryApiIntrospection();

  const queryAst = useMemo(() => buildQueryApiQueryAst(queryFormState), [queryFormState]);
  const throttledQueryAst = useThrottle(queryAst, FORM_STATE_THROTTLE_DURATION);

  const canFinalize =
    queryApiIntrospection.status === QueryResultType.SUCCESS &&
    queryAst != null &&
    queryApiIntrospection.getQueryAstValidationErrors(queryAst).length === 0 &&
    queryFormState.entityListingFieldName != null &&
    dataFrameNameState.name !== '';

  useAutoUpdatePreview(
    throttledQueryAst == null ? null : {queryAst: throttledQueryAst},
    (requestData, setAsyncState) => {
      assert(
        queryApiIntrospection.status === QueryResultType.SUCCESS,
        'Tried to generate a preview before introspection finished'
      );

      const validationErrors = queryApiIntrospection.getQueryAstValidationErrors(requestData.queryAst);
      if (validationErrors.length > 0) {
        Logging.warn('Tried to preview an invalid query', {
          extra: {
            query: astToString(requestData.queryAst),
            errors: validationErrors.map((error) => error.message),
          },
        });
      }

      return makeAsyncCall({
        args: [requestData],
        asyncState: previewState,
        makeCall: ({queryAst}) => generateDatasetPreview(astToString(queryAst)),
        setAsyncState,
      });
    },
    setPreviewState
  );

  function handleClearState(): void {
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setQueryFormState(DEFAULT_QUERY_FORM_STATE);
    setPreviewState(NOT_FETCHED_ASYNC_STATE);
  }

  // Memoized so that RegistrySchemasStepperForm doesn't rerender unnecessarily and cause the QueryApiFilters
  // to rerender and close if already opened.
  const handleChangeQueryFormState = useCallback(
    (newFormState: FormState) => {
      const totalFields =
        newFormState.entityListingFieldName == null
          ? 0
          : countSelectedFields(newFormState.entityFieldSelections.selections) +
            sum(
              newFormState.relatedResultsFormStates.map((resultFormState) =>
                countSelectedFields(resultFormState.backlinkSelections.selections)
              )
            );
      if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
        notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
          duration: 4,
        });
      } else {
        setQueryFormState(newFormState);
      }
    },
    [setQueryFormState]
  );

  const onFinalize = async function handleReplaceAndRunStep(): Promise<void> {
    assert(queryAst != null && dataFrameNameState.name !== '', 'Tried to finalize with incomplete form');

    setIsFinalizing(true);
    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        {
          stepType: PipelineStepType.REGISTRY_SCHEMA_DATASET,
          stepVersion: 0,
          stepConfig: {
            dataFrameName: dataFrameNameState.name,
            query: astToString(queryAst),
            queryDescription: await formStateToHumanReadableString(queryFormState, queryApiIntrospection),
          },
        },
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      onCloseStepInitializationView(getDefaultMainViewItemForStep(newStep));
      handleClearState();
      setIsFinalizing(false);
    } catch (err) {
      setIsFinalizing(false);
      throw err;
    }
  };

  const previewDataFrame = getMostRecentData(previewState)?.dataFrame ?? null;

  if (previewDataFrame?.columns) {
    unselectColumnsIfColumnIsNotInTemplateColumns(
      selectedColumns,
      previewDataFrame.columns,
      setSelectedColumns
    );
  }

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={clientStateId}
      dataFrames={previewDataFrame == null ? [] : [{...previewDataFrame, name: dataFrameNameState.name}]}
      disableFinalizeButton={!canFinalize}
      isFinalizing={isFinalizing}
      isLoadingPreview={previewState.status === AsyncStatus.LOADING}
      messages={buildErrorAndWarningMessages(previewState)}
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select a registry schema to see a preview of your table.',
      }}
      defaultDataFrameTableMode={DataFrameTableMode.TEXT}
      onCancel={() => {
        handleClearState();
        onCloseStepInitializationView();
      }}
      // TODO(BNCH-53649): Explore reusing the finalization logic from CreateDataFrameFromAnalyticsBlockView
      onFinalize={onFinalize}
      title="Registry schemas"
      toolbar={
        getEnabledDatasetCreationMethods().length > 1 && (
          <ChooseDifferentDataFrameCreationMethodButton
            onOpenStepInitializationView={(newSelectedItem: {
              type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
            }) => {
              handleClearState();

              onOpenStepInitializationView({
                ...newSelectedItem,
                stepIdToReplace: selectedItem.stepIdToReplace,
              });
            }}
          />
        )
      }
    >
      {queryApiIntrospection.status === QueryResultType.ERROR ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />
      ) : queryApiIntrospection.status === QueryResultType.PENDING ? (
        <CenteredHorizontalThrobber />
      ) : (
        <RegistrySchemaStepperForm
          queryApiIntrospection={queryApiIntrospection}
          queryFormState={queryFormState}
          setQueryFormState={handleChangeQueryFormState}
          setIsManuallyChanged={setIsManuallyChanged}
          dataFrameNameState={dataFrameNameState}
          setDataFrameNameState={setDataFrameNameState}
          columnMappingProps={
            selectedItem.stepIdToReplace != null
              ? {
                  templateDataFrameColumns,
                  newDataFrameColumns: previewDataFrame?.columns ?? null,
                  newDataFrameState: previewState.status,
                  selectedColumns,
                  setSelectedColumns,
                  isManuallyChanged,
                  setIsManuallyChanged,
                }
              : undefined
          }
        />
      )}
    </StepConfigurationViewTemplate>
  );
}
